import i18n from '@/i18n';
import dayjs from 'dayjs';
import store from '@/store';
import H_file from '@/helpers/file';
import H_time from '@/helpers/time';
import H_checklist from '@/helpers/checklist';

const CRITICAL_ANSWER_REMARK_RISK_SCORES = [20, 21, 22, 23];

const ANSWER_VALUE_VALIDATORS = {
  'single-choice': (value) => Array.isArray(value) && value.length > 0,
  num: (value) => value !== '' && value !== null,
  date: (value) => value !== null,
  text: (value) => value !== '' && value !== null,
  default: (value) => value !== null,
};

const TITLE_CHIPS = {
  keypoint: { color: 'green', name: '重點關注' },
  notInStats: { color: 'yellow', name: '不列入統計' },
  modified: { color: 'red', name: '題目變更' },
  requiredRemark: { color: 'red', name: '尚未填寫備註' },
};

const READ_ICON_CONFIG = {
  icon: 'icon-visibility',
  color: 'light-gray',
};

function buildAssignmentTitleObject(title, keypoint, isInStats) {
  const chips = [];
  if (keypoint) chips.push({ ...TITLE_CHIPS.keypoint });
  if (!isInStats) chips.push({ ...TITLE_CHIPS.notInStats });
  return { text: title, chips };
}

function buildRecordDisplayAnswerResult(quesTypeValue, answerValue) {
  switch (quesTypeValue) {
    case 'single-choice':
      return Array.isArray(answerValue) && answerValue.length > 0 ? answerValue[0].name : '';
    case 'text':
      return answerValue && answerValue.length > 3
        ? `${answerValue.slice(0, 3)}...`
        : answerValue;
    case 'date':
      return H_time.getDateDashFormat(answerValue, 'YYYY-MM-DD HH:mm:ss');
    default:
      return answerValue;
  }
}

function getRisk25BatchOption(ques) {
  if (ques.batchObj) return ques.batchObj;
  if (!Array.isArray(ques.optionList)) return null;
  const option = ques.optionList.find(item => item.riskValue == 25);
  if (!option) return null;
  return {
    id: option.id,
    name: option.title,
  };
}

function buildBatchPassState({ quesTypeValue, risk25Option, checkers, currentUserId, recordAnswer }) {
  const isBatchPassable =
  quesTypeValue === 'single-choice' &&
  !!risk25Option &&
  checkers.some(checker => checker.id === currentUserId) &&
  (!recordAnswer || recordAnswer.updated_user?.id === currentUserId);
  return { isBatchPassable };
}

function buildRecordAnswerBatchPassState(resultQues, recordAnswer, currentUserId) {
  const risk25Option = getRisk25BatchOption(resultQues);
  const batchPassState = buildBatchPassState({
    quesTypeValue: resultQues.quesType.value,
    risk25Option,
    checkers: resultQues.checkers,
    currentUserId,
    recordAnswer,
  });
  const { isBatchPassable } = batchPassState;
  if (isBatchPassable) {
    return {
      ...batchPassState,
      isChecked: resultQues.isChecked === undefined ? false : resultQues.isChecked,
      batchObj: risk25Option,
    };
  }
  if (resultQues.isChecked === undefined) return batchPassState;
  return {
    ...batchPassState,
    isChecked: false,
  };
}

function applyRecordAnswerToQues(resultQues, recordAnswer, deps) {
  const {
    versionUpdatedAt,
    quesType,
    titleObject,
    checkers,
    currentUserId,
    setRiskLevelFromScore,
  } = deps;
  const quesTypeValue = quesType.value;
  const {
    id: answerId,
    risk_level: riskLevelScore,
    question_value,
    updated_user: answerer,
    updated_at: updatedAt,
    ip,
    lat,
    lng,
    completed_at,
    completed_user: completedUser,
    is_completed,
    question_type_setting: AnswerQuesType,
    remark: answerRemark,
  } = recordAnswer;
  const timeText = `(${H_time.getDateDashFormat(updatedAt, 'YYYY-MM-DD HH:mm:ss')})`;
  const answererWithMeta = { ...answerer, time: timeText };
  const checkersWithAnswerer = buildCheckersWithUpdatedTime(checkers, updatedAt, answerer);
  const isNeedCheckAns = H_time.isAfter(dayjs(versionUpdatedAt), dayjs(updatedAt));
  if (isNeedCheckAns) titleObject.chips.push({ ...TITLE_CHIPS.modified });
  const answerValueList = Array.isArray(question_value) ? question_value : [];
  const answerValue = quesType.value === 'single-choice'
    ? answerValueList.map(val => val.id)
    : question_value;
    const batchPassState = buildRecordAnswerBatchPassState(resultQues, recordAnswer, currentUserId);
  return {
    ...resultQues,
    ...batchPassState,
    titleObject,
    answerer: answererWithMeta,
    answerId,
    checkers: checkersWithAnswerer,
    answerValue,
    answerResult: buildRecordDisplayAnswerResult(quesTypeValue, question_value),
    answerRisk: setRiskLevelFromScore(riskLevelScore),
    answerRemark,
    ip,
    lat,
    lng,
    isAnswerCurrentUser: answerer.id === currentUserId,
    isNeedCheckAns,
    completedAt: H_time.getDateDashFormat(completed_at, 'YYYY-MM-DD HH:mm:ss'),
    completedUser,
    isCompleted: is_completed === 1,
    AnswerQuesType,
  };
}

function buildCheckersWithUpdatedTime(checkers, updatedAt, updatedUser) {
  if (!updatedUser) return checkers;
  const timeText = `(${H_time.getDateDashFormat(updatedAt, 'YYYY-MM-DD HH:mm:ss')})`;
  return checkers.map(checker =>
    checker.id === updatedUser.id
      ? { ...checker, ...updatedUser, time: timeText }
      : checker
  );
}

export default {
  TITLE_CHIPS,
  buildCheckersWithUpdatedTime,
  setAssignment(rawData) {
    const {
      id,
      checkers,
      checklist,
      checklist_version: checklistVersion,
      general_schedule_setting: scheduleSetting,
      reviewers,
      record_at,
      checklist_record,
    } = rawData;
    const {
      id: checklistId,
      name,
      system_subclasses = [],
      factory_system_subclasses = [],
    } = checklist;
    return {
      id,
      name,
      checkers: H_checklist.setAnswerReviewRecord(checkers, 'answer', false),
      reviewers: H_checklist.setAnswerReviewRecord(reviewers, 'review', false),
      scheduleSettingId: scheduleSetting.id,
      checklistId,
      checklistVersionId: checklistVersion.id,
      multiSystemSubclasses: factory_system_subclasses.concat(system_subclasses),
      date: H_time.getDateDashFormat(record_at),
      duration: H_checklist.setAssignmentDuration(rawData),
      recordId: checklist_record?.id,
    };
  },
  setQuesAndAnswer(rawQuesList, rawAnsList) {
    const rawAnsMap = rawAnsList?.length > 0 ? new Map(rawAnsList.map(ans => [ans.checklist_question_version?.id, ans])) : new Map();
    const currentUser = store.state.stone_auth.currentUser;
    const currentUserId = currentUser.id;
    const noAnswerRiskLevel = this.setRiskLevelFromScore(null, true);
    return rawQuesList.map(rawQues => {
      const recordAnswer = rawAnsMap.get(rawQues.id);
      const {
        id,
        sequence,
        title,
        question_setting: quesSetting,
        updated_at: versionUpdatedAt,
        question_type_setting: quesType,
        keypoint,
        effects,
        factory_effects: factoryEffects,
        is_in_stats: isInStats,
        question_setting_items,
        checklist_question
      } = rawQues;
      const quesTypeValue = quesType.value;
      const titleObject = buildAssignmentTitleObject(title, keypoint, isInStats);
      const checkers = Array.isArray(quesSetting?.checkers) ? quesSetting.checkers : [currentUser];
      const risk25Option = Array.isArray(question_setting_items)
        ? question_setting_items.find(item => item.risk_level == 25)
        : null;
      const { isBatchPassable } = buildBatchPassState({
        quesTypeValue,
        risk25Option,
        checkers,
        currentUserId,
        recordAnswer,
      });
      const baseQues = {
        id,
        sequence,
        title,
        titleObject,
        answerer: null,
        answerId: null,
        quesType,
        answerRemark: null,
        answerImages: [],
        answerValue: null,
        answerResult: null,
        answerRisk: noAnswerRiskLevel,
        score: null,
        read: { ...READ_ICON_CONFIG },
        isFetched: false,
        effects: [effects, factoryEffects],
        factoryEffects,
        isInStats,
        isInStatsText: isInStats ? '是' : '否',
        keypoint: keypoint === 1
          ? {
              color: 'green',
              textContent: i18n.t('重點關注'),
              text: i18n.t('重點關注'),
            }
          : null,
        checkers,
        versionUpdatedAt,
        isBatchPassable,
        ip: null,
        lat: null,
        lng: null,
        quesId: checklist_question?.id,
        isAnswerCurrentUser: false,
        optionList: quesTypeValue === 'single-choice' ? H_checklist.setSingleChoiceOptionList(question_setting_items) : null,
      };
      const batchPassableQues = isBatchPassable
        ? {
            isChecked: false,
            batchObj: {
              id: risk25Option.id,
              name: risk25Option.name,
            },
          }
        : {};
      const resultQues = {
        ...baseQues,
        ...batchPassableQues,
      };
      if (!recordAnswer) {
        return {
          ...resultQues,
          riskLevel: noAnswerRiskLevel,
        };
      }
      return applyRecordAnswerToQues(resultQues, recordAnswer, {
        versionUpdatedAt,
        quesType,
        titleObject,
        checkers,
        currentUserId,
        setRiskLevelFromScore: score => this.setRiskLevelFromScore(score),
      });
    });
  },
  setAnswerResult(answerQue) {
    const {quesType, answerValue, optionList} = answerQue;
    if (answerValue === '' || answerValue === undefined || answerValue === null) return null;
    let answerResult = null;
    if (quesType.value === 'text') answerResult = answerValue && answerValue.length > 3 ? answerValue.slice(0, 3) : answerValue;
    else if (quesType.value === 'date') answerResult = H_time.getDateDashFormat(answerValue, 'YYYY-MM-DD HH:mm:ss');
    else if (quesType.value === 'num') answerResult = answerValue;
    else if (quesType.value === 'single-choice' && optionList && optionList.length && answerValue && Array.isArray(answerValue)) {
      let answerOption= {};
      optionList.forEach(option => {
        if (Object.keys(answerOption).length) return;
        if (option.id === answerValue[0]) answerOption = option;
        else if (option.subOptionList && option.subOptionList.length) {
          option.subOptionList.forEach(subOption => {
            if (Object.keys(answerOption).length) return;
            if (subOption.id === answerValue[0]) answerOption = subOption;
          })
        }
      })
      answerResult = answerOption.title;
    }
    return answerResult;
  },
  setAnswerRisk(answerQue) {
    const {quesType, answerValue, optionList, conditionList, numConditionType, controlLimitDataLower, controlLimitDataUpper, specLimitDataLower, specLimitDataUpper} = answerQue;
    const riskLevelList = store.state.model.checklistRiskLevelList.filter(risk => risk.type === 'question');
    const notInvolveRisk = riskLevelList.find(risk => risk.code === 'checklist_question_not_involve_risk');
    const notAnsweredRisk = riskLevelList.find(risk => risk.code == 'checklist_question_no_answer');
    if (answerValue === '' || answerValue === undefined || answerValue === null) return notAnsweredRisk;
    let answerRisk = {};
    if (quesType.value === 'text' || quesType.value === 'date') answerRisk = notInvolveRisk;
    else if (quesType.value === 'num' && numConditionType) {
      if (answerValue === null || answerValue == '') answerRisk = notAnsweredRisk;
      if (numConditionType.type === 'num-only') answerRisk = notInvolveRisk;
      else if (numConditionType.type === 'control-limit') {
        let riskLevelScore = null;
        const floatScore = parseFloat(answerValue);
        const _controlLimitDataLower = parseFloat(controlLimitDataLower);
        const _controlLimitDataUpper = parseFloat(controlLimitDataUpper);
        const _specLimitDataLower = parseFloat(specLimitDataLower);
        const _specLimitDataUpper = parseFloat(specLimitDataUpper);
        if (
          !Number.isNaN(_controlLimitDataLower) &&
          !Number.isNaN(_controlLimitDataUpper) &&
          !Number.isNaN(_specLimitDataLower) &&
          !Number.isNaN(_specLimitDataUpper)
        ) {
          const upperMiddle =
          (_controlLimitDataUpper + _specLimitDataUpper) / 2;
          const lowerMiddle =
          (_controlLimitDataLower + _specLimitDataLower) / 2;
          if (
            floatScore >= _controlLimitDataLower &&
            floatScore <= _controlLimitDataUpper
          ) {
            riskLevelScore = 25;
          } else if (floatScore > _specLimitDataUpper || floatScore < _specLimitDataLower) {
            riskLevelScore = 23;
          } else if (
            (
              floatScore < _controlLimitDataLower &&
              floatScore >= lowerMiddle
            ) ||
            (
              floatScore > _controlLimitDataUpper &&
              floatScore <= upperMiddle
            )
          ) {
            riskLevelScore = 21;
          } else if (
            (
              floatScore < lowerMiddle &&
              floatScore >= _specLimitDataLower
            ) ||
            (
              floatScore > upperMiddle &&
              floatScore <= _specLimitDataUpper
            )
          ) {
            riskLevelScore = 22;
          }
        } else if (
          (!Number.isNaN(_controlLimitDataLower) &&
          !Number.isNaN(_controlLimitDataUpper)) ||
          (!Number.isNaN(_specLimitDataLower) &&
          !Number.isNaN(_specLimitDataUpper) )
        ) {
          const lowerLimit = Number.isNaN(_controlLimitDataLower) ? _specLimitDataLower : _controlLimitDataLower;
          const upperLimit = Number.isNaN(_controlLimitDataUpper) ? _specLimitDataUpper : _controlLimitDataUpper;
          if (floatScore > upperLimit || floatScore < lowerLimit) {
            riskLevelScore = 23;
          } else if (floatScore <= upperLimit && floatScore >= lowerLimit) {
            riskLevelScore = 25;
          }
        } else if (
          Number.isNaN(_controlLimitDataLower) &&
          Number.isNaN(_controlLimitDataUpper) &&
          Number.isNaN(_specLimitDataLower) &&
          Number.isNaN(_specLimitDataUpper)
        ) {
          riskLevelScore = 26;
        }
        answerRisk = riskLevelList.find(risk => risk.score == riskLevelScore);
      } else if (numConditionType.type === 'custom-num') {
        let riskLevelScore = null;
        const floatAnswerValue = parseFloat(answerValue);
        conditionList.forEach(({lowVal, highVal, riskValue, operatorValue}) => {
          if (
            (operatorValue === 'in' && floatAnswerValue <= highVal && floatAnswerValue >= lowVal) ||
            (operatorValue === 'not_in' && (floatAnswerValue > highVal || floatAnswerValue < lowVal)) ||
            (operatorValue === 'eq' && floatAnswerValue == lowVal) ||
            (operatorValue === 'neq' && floatAnswerValue != lowVal) ||
            (operatorValue === 'gt' && floatAnswerValue > lowVal) ||
            (operatorValue === 'gte' && floatAnswerValue >= lowVal) ||
            (operatorValue === 'lt' && floatAnswerValue < lowVal) ||
            (operatorValue === 'lte' && floatAnswerValue <= lowVal)
          ) riskLevelScore = riskValue;
        })
        answerRisk = riskLevelScore === null ? notInvolveRisk : riskLevelList.find(risk => risk.score == riskLevelScore);
      }
    } else if (quesType.value === 'single-choice' && optionList && optionList.length && answerValue && Array.isArray(answerValue)) {
      let answerOption= {};
      optionList.forEach(option => {
        if (Object.keys(answerOption).length) return;
        if (option.id === answerValue[0]) answerOption = option;
        else if (option.subOptionList && option.subOptionList.length) {
          option.subOptionList.forEach(subOption => {
            if (Object.keys(answerOption).length) return;
            if (subOption.id === answerValue[0]) answerOption = subOption;
          })
        }
      })
      answerRisk = answerOption.riskValue === null ? notInvolveRisk : riskLevelList.find(risk => risk.score == answerOption.riskValue);
    }
    return answerRisk;
  },
  async setSubmitData(submitParams) {
    const {
      submitType,
      assignmentId,
      reviewers,
      drop,
      linkId,
    } = submitParams;
    const currentFactoryId = store.state.app.factoryId;
    let data = {
      factory: currentFactoryId,
      remark: '',
      images: [],
      reviewers: reviewers.map(reviewer => reviewer.id ? reviewer.id : reviewer),
    }
    if ( assignmentId ) {
      data.checklist_assignment = assignmentId;
    }
    if ( linkId ) data.link = linkId;
    if ( submitType === 'drop' ) {
      data = {
        ...data,
        remark: drop.remark,
        images: H_file.setSubmitInModule(drop.images),
        status: '1',
        checklist_record_answers: [],
      };
    }
    return data;
  },
  setTemporaryQuesList(rawQuesList) {
    const { id: currentUserId, name: currentUserName, avatar: currentUserAvatar } = store.state.stone_auth.currentUser;
    const noAnswerRiskLevel = this.setRiskLevelFromScore(null, true);
    const checker = {
      id: currentUserId,
      name: currentUserName,
      avatar: currentUserAvatar,
    };
    const quesList = rawQuesList.map(rawQues => {
      const {
        id,
        sequence,
        title,
        last_version: lastVersion,
        updated_at: updatedAt,
      } = rawQues;
      const {
        id: versionId,
        question_type_setting: quesType,
        keypoint,
        effects,
        factory_effects: factoryEffects,
        is_in_stats: isInStats,
        updated_at: versionUpdatedAt,
        question_setting_items,
      } = lastVersion;
      const titleObject = {
        text: title,
        chips: [],
      };
      if (keypoint) titleObject.chips.push({ ...TITLE_CHIPS.keypoint });
      if (!isInStats) titleObject.chips.push({ ...TITLE_CHIPS.notInStats });
      const risk25Option = Array.isArray(question_setting_items)
        ? question_setting_items.find(item => item.risk_level == 25)
        : null;
      const isBatchPassable = quesType.value === 'single-choice' && !!risk25Option;
      const resultQues = {
        id,
        versionId,
        sequence,
        title,
        titleObject,
        answerer: null,
        answerId: null,
        quesType,
        answerRemark: null,
        answerImages: [],
        answerValue: null,
        answerResult: null,
        answerRisk: noAnswerRiskLevel,
        score: null,
        read: { ...READ_ICON_CONFIG },
        isFetched: false,
        effects,
        factoryEffects,
        riskLevel: noAnswerRiskLevel,
        checkers: [checker],
        isInStats,
        keypoint,
        updatedAt,
        versionUpdatedAt,
        isBatchPassable,
      };
      if (!isBatchPassable) return resultQues;
      return {
        ...resultQues,
        isChecked: false,
        batchObj: {
          id: risk25Option.id,
          name: risk25Option.name,
        },
      };
    });
    return quesList;
  },
  setRiskLevelFromScore(score, isUnAnswered) {
    const list = store.state.model.checklistRiskLevelList;
    for (let i = 0; i < list.length; i++) {
      const risk = list[i];
      if (risk.type !== 'question') continue;
      if (isUnAnswered) {
        if (risk.code === 'checklist_question_no_answer') return risk;
      } else if (score === null) {
        if (risk.code === 'checklist_question_not_involve_risk') return risk;
      } else if (risk.code !== 'checklist_question_no_answer' && risk.score == score) {
        return risk;
      }
    }
  },
  setRiskLevelText(quesType, riskLevel) {
    const {text,subtext} = riskLevel;
    return quesType === 1 ? i18n.t(subtext) : i18n.t(text);
  },
  setIsHasAnswerValue(ques) {
    const { quesType, answerValue } = ques;
    const validate =
      ANSWER_VALUE_VALIDATORS[quesType.value] || ANSWER_VALUE_VALIDATORS.default;
    return validate(answerValue);
  },
  setIsOptionNeedAnswerRemark(ques) {
    const { quesType, answerValue, optionList } = ques;
    if (
      quesType.value !== 'single-choice' ||
      !optionList ||
      !Array.isArray(answerValue) ||
      answerValue.length === 0 ||
      !answerValue[0]
    ) return false;
    const selectedId = answerValue[0];
    for (let i = 0; i < optionList.length; i++) {
      const option = optionList[i];
      if (option.id === selectedId) return option.requiredRemark;
      const subs = option.subOptionList;
      if (!subs) continue;
      for (let j = 0; j < subs.length; j++) {
        if (subs[j].id === selectedId) return subs[j].requiredRemark;
      }
    }
    return false;
  },
  setIsNeedAnswerRemark(ques) {
    if (!this.setIsHasAnswerValue(ques)) return false;
    const { answerRemark, answerRisk } = ques;
    if (answerRemark && answerRemark.replace(/\s+/g, '').length > 0) return false;
    return (
      this.setIsOptionNeedAnswerRemark(ques) ||
      !!(answerRisk && CRITICAL_ANSWER_REMARK_RISK_SCORES.some(s => s == answerRisk.score))
    );
  },
  updateAnswer(quesList, rawAnsList) {
    if (rawAnsList?.length === 0) return quesList;
    const rawAnsMap = rawAnsList?.length > 0
      ? new Map(rawAnsList.map(ans => [ans.checklist_question_version?.id, ans]))
      : new Map();
    const currentUserId = store.state.stone_auth.currentUser.id;
    return quesList.map(ques => {
      const recordAnswer = rawAnsMap.get(ques.id);
      if (!recordAnswer) return ques;
      const titleObject = {
        text: ques.title,
        chips: [
          ...(ques.keypoint ? [{ ...TITLE_CHIPS.keypoint }] : []),
          ...(!ques.isInStats ? [{ ...TITLE_CHIPS.notInStats }] : []),
        ],
      };
      return applyRecordAnswerToQues(ques, recordAnswer, {
        versionUpdatedAt: ques.versionUpdatedAt,
        quesType: ques.quesType,
        titleObject,
        checkers: ques.checkers,
        currentUserId,
        setRiskLevelFromScore: score => this.setRiskLevelFromScore(score),
      });
    });
  },
}
